import { workletSource } from 'virtual:kpv-worklet';

export type Routing = 'chain' | 'parallel';

/**
 * The shared audio backbone for a multi-deck session: one AudioContext, the
 * KPV worklet module (loaded once), the live input, and the wiring between
 * deck nodes.
 *
 *   chain    : input → deck1 → deck2 → … → destination (default — pads stack)
 *   parallel : input → every deck → destination (decks sum side by side)
 */
export class AudioSystem {
  private ctx_: AudioContext | null = null;
  private inputSource: MediaStreamAudioSourceNode | null = null;
  private inputStream: MediaStream | null = null;
  private wired: AudioWorkletNode[] = [];
  private routing_: Routing = 'chain';

  onInputChanged: ((label: string | null) => void) | null = null;

  get ctx(): AudioContext {
    if (!this.ctx_) throw new Error('audio system not started');
    return this.ctx_;
  }

  get running(): boolean {
    return this.ctx_ !== null && this.ctx_.state === 'running';
  }

  get sampleRate(): number {
    return this.ctx_?.sampleRate ?? 48000;
  }

  get contextLatencyMs(): number {
    if (!this.ctx_) return 0;
    const out = (this.ctx_ as AudioContext & { outputLatency?: number }).outputLatency ?? 0;
    return ((this.ctx_.baseLatency ?? 0) + out) * 1000;
  }

  get inputLabel(): string | null {
    return this.inputStream?.getAudioTracks()[0]?.label ?? null;
  }

  /** Must originate from a user gesture (autoplay policy). Idempotent. */
  async start(): Promise<void> {
    if (this.ctx_) {
      void this.ctx_.resume();
      return;
    }
    const ctx = new AudioContext({ latencyHint: 0 });
    const blob = new Blob([workletSource], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    try {
      await ctx.audioWorklet.addModule(url);
    } finally {
      URL.revokeObjectURL(url);
    }
    this.ctx_ = ctx;

    // Autoplay: retry resume on the next real interaction; never block boot.
    void ctx.resume();
    if (ctx.state !== 'running') {
      const kick = (): void => void ctx.resume();
      const off = (): void => {
        document.removeEventListener('pointerdown', kick);
        document.removeEventListener('keydown', kick);
      };
      document.addEventListener('pointerdown', kick);
      document.addEventListener('keydown', kick);
      ctx.addEventListener('statechange', () => {
        if (ctx.state === 'running') off();
      });
    }
  }

  /** (Re)connect deck nodes according to the routing mode. */
  wire(nodes: AudioWorkletNode[], routing: Routing): void {
    this.routing_ = routing;
    for (const n of this.wired) n.disconnect();
    this.inputSource?.disconnect();
    this.wired = [...nodes];
    if (nodes.length === 0) return;
    const dest = this.ctx.destination;
    if (routing === 'chain') {
      for (let i = 0; i < nodes.length - 1; i++) nodes[i]!.connect(nodes[i + 1]!);
      nodes[nodes.length - 1]!.connect(dest);
      if (this.inputSource) this.inputSource.connect(nodes[0]!);
    } else {
      for (const n of nodes) {
        n.connect(dest);
        if (this.inputSource) this.inputSource.connect(n);
      }
    }
  }

  async enableInput(deviceId?: string): Promise<string | null> {
    this.disableInput(false);
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 2,
      },
    });
    this.inputSource = this.ctx.createMediaStreamSource(stream);
    this.inputStream = stream;
    this.wire(this.wired, this.routing_); // reconnect with input in place
    const label = this.inputLabel;
    this.onInputChanged?.(label);
    return label;
  }

  disableInput(notify = true): void {
    this.inputSource?.disconnect();
    this.inputSource = null;
    if (this.inputStream) {
      for (const t of this.inputStream.getTracks()) t.stop();
      this.inputStream = null;
    }
    if (notify) this.onInputChanged?.(null);
  }

  static async listInputDevices(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === 'audioinput');
  }
}
